import { z } from "zod";
import {
  POSEUR_TASK_TYPES,
  RENDER_MEDIA_KINDS,
  RENDER_REVIEW_STATUSES,
  SUPPORT_TYPES,
  TASK_STATUSES,
  TASK_TYPES,
  USER_ROLES,
  VT_PROJECT_KINDS,
  VT_RGA_LEVELS,
  type TaskStatus,
} from "./enums.js";

export const userRoleSchema = z.enum(USER_ROLES);
export const taskTypeSchema = z.enum(TASK_TYPES);
export const taskStatusSchema = z.enum(TASK_STATUSES);
export const renderReviewStatusSchema = z.enum(RENDER_REVIEW_STATUSES);
export const renderMediaKindSchema = z.enum(RENDER_MEDIA_KINDS);

export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const taskScheduledRangeSchema = z
  .object({
    start: isoDateTimeSchema,
    end: isoDateTimeSchema,
  })
  .refine((v) => new Date(v.start) < new Date(v.end), {
    message: "scheduled_range.start must be before end",
  });

const slotInputSchema = z
  .object({
    start: isoDateTimeSchema,
    end: isoDateTimeSchema,
  })
  .refine((v) => new Date(v.start) < new Date(v.end), {
    message: "slot start must be before end",
  });

export const taskSlotProposalsProposePayloadSchema = z.object({
  slots: z.array(slotInputSchema).min(1).max(20),
});

export const taskSlotSelectPayloadSchema = z.object({
  slot_id: z.string().min(1).max(120),
});

// Saisie de l'heure d'intervention par le relais : un instant de DÉMARRAGE, pas
// une plage. Choix produit : l'heure de fin n'est ni demandée ni stockée — la
// durée d'une visite n'est pas connue à l'avance et n'engage personne. La valeur
// atterrit dans tasks.scheduled_at, qui porte exactement cette sémantique.
export const taskScheduleConfirmPayloadSchema = z.object({
  start: isoDateTimeSchema,
});

// Forme stockée dans tasks.slot_proposals (jsonb).
export const taskSlotProposalsSchema = z.object({
  slots: z
    .array(
      z.object({
        id: z.string().min(1).max(120),
        start: isoDateTimeSchema,
        end: isoDateTimeSchema,
      }),
    )
    .min(1),
  proposed_at: isoDateTimeSchema,
  selected_slot_id: z.string().min(1).max(120).nullable(),
});

export const userProfileUpdateSchema = z.object({
  first_name: z.string().min(1).max(120).optional(),
  last_name: z.string().min(1).max(120).optional(),
  phone: z.string().min(3).max(40).nullable().optional(),
  department: z.string().min(1).max(120).nullable().optional(),
  avatar_path: z.string().min(1).max(500).nullable().optional(),
});

// Position d'un relais sur la carte admin. Les coordonnées vont par paire :
// les deux nulles = retour à la position du département.
export const userLocationUpdateSchema = z
  .object({
    location_label: z.string().min(1).max(200).nullable(),
    location_address: z.string().min(1).max(300).nullable(),
    latitude: z.number().min(-90).max(90).nullable(),
    longitude: z.number().min(-180).max(180).nullable(),
    radius_km: z.number().int().min(1).max(500),
  })
  .refine((v) => (v.latitude === null) === (v.longitude === null), {
    message: "latitude et longitude vont ensemble",
    path: ["longitude"],
  })
  .refine((v) => v.location_address === null || v.latitude !== null, {
    message: "une adresse sans coordonnées ne place pas le relais",
    path: ["location_address"],
  });

export const userInvitePayloadSchema = z.object({
  email: z.string().email(),
  first_name: z.string().min(1).max(120).optional(),
  last_name: z.string().min(1).max(120).optional(),
  phone: z.string().min(3).max(40).optional(),
  role: userRoleSchema,
});

// Stats d'activité d'un relais (comptes par statut). byStatus est garanti
// complet (les 8 statuts, 0 par défaut) côté serveur — cf. statsForActor.
const nonNegInt = z.number().int().nonnegative();
export const userActivityStatsSchema = z.object({
  total: nonNegInt,
  byStatus: z.record(taskStatusSchema, nonNegInt),
});
export type UserActivityStats = {
  total: number;
  byStatus: Record<TaskStatus, number>;
};

export const contractCreatePayloadSchema = z.object({
  contract_number: z.string().min(1).max(120),
  type: z.string().min(1).max(60).optional(),
  start_date: isoDateSchema,
  end_date: isoDateSchema.nullable().optional(),
  file_path: z.string().min(1).nullable().optional(),
});

export const supportTypeSchema = z.enum(SUPPORT_TYPES);

export const controlPointSpecSchema = z.object({
  id: z.string().min(1).max(120),
  label: z.string().min(1).max(300),
  requirePhoto: z.boolean(),
});

export const taskUploadSchema = z.object({
  name: z.string().min(1).max(300),
  storage_path: z.string().min(1).max(500),
});

export const taskSpecSchema = z.object({
  supportType: supportTypeSchema.optional(),
  supportTypeOther: z.string().max(200).optional(),
  points: z.array(controlPointSpecSchema).optional(),
  requiredPhotoItems: z.array(z.string().min(1).max(120)).optional(),
  projectContext: z
    .object({
      projectKind: z.enum(VT_PROJECT_KINDS),
      surfaceM2: z.number().positive(),
    })
    .optional(),
  rgaAssessment: z
    .object({
      level: z.enum(VT_RGA_LEVELS),
      source: z.enum(["georisques", "manuel"]),
      provider: z.string().min(1).max(120),
      queriedAt: z.string().min(1).max(40),
      address: z.string().max(300).optional(),
      lat: z.number().optional(),
      lon: z.number().optional(),
      codeExposition: z.string().max(4).optional(),
      geocodeScore: z.number().optional(),
    })
    .optional(),
  foundationMatrixVersion: z.string().max(40).optional(),
  attachedDocumentIds: z.array(z.string().min(1).max(200)).default([]),
  uploads: z.array(taskUploadSchema).default([]),
});

export const taskCreatePayloadSchema = z
  .object({
    type: taskTypeSchema,
    project_id: z.string().min(1).max(120),
    supervisor_id: z.string().uuid(),
    assignee_id: z.string().uuid().nullable().optional(),
    scheduled_range: taskScheduledRangeSchema.nullable().optional(),
    scheduled_at: isoDateTimeSchema.nullable().optional(),
    description: z.string().min(1).max(4000),
    address: z.string().min(1).max(300),
    client_first_name: z.string().min(1).max(120),
    client_last_name: z.string().min(1).max(120),
    spec: taskSpecSchema.default({ attachedDocumentIds: [], uploads: [] }),
    additional_supervisor_ids: z.array(z.string().uuid()).default([]),
  })
  .superRefine((val, ctx) => {
    if (val.type !== "reception_supports") return;
    if (!val.spec.supportType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["spec", "supportType"],
        message: "supportType requis pour reception_supports",
      });
    }
    if (!val.spec.points || val.spec.points.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["spec", "points"],
        message: "au moins un point de contrôle requis",
      });
    }
  });

export const taskUpdatePayloadSchema = z.object({
  assignee_id: z.string().uuid().nullable().optional(),
  scheduled_range: taskScheduledRangeSchema.nullable().optional(),
  scheduled_at: isoDateTimeSchema.nullable().optional(),
  description: z.string().min(1).max(4000).optional(),
  address: z.string().min(1).max(300).optional(),
  client_first_name: z.string().min(1).max(120).optional(),
  client_last_name: z.string().min(1).max(120).optional(),
  attached_document_ids: z.array(z.string().min(1).max(200)).optional(),
  uploads: z.array(taskUploadSchema).optional(),
  additional_supervisor_ids: z.array(z.string().uuid()).optional(),
});

export const taskRefusePayloadSchema = z.object({
  reason: z.string().min(1).max(2000),
});

// Forçage admin du statut. expected_status = verrou optimiste : la fiche d'où
// part l'admin doit encore refléter l'état réel de la mission.
export const taskStatusOverridePayloadSchema = z.object({
  status: taskStatusSchema,
  expected_status: taskStatusSchema,
  note: z.string().max(2000).optional(),
});

/**
 * Rouvrir un compte rendu validé pour le corriger.
 *
 * `completed` est terminal à dessein : il déclenche le PDF client et la
 * facturation (le contrôle de gestion compte les missions dues sur les
 * validées). Rouvrir, c'est donc revenir sur un document peut-être déjà envoyé
 * et une mission peut-être déjà facturée — le motif est obligatoire et il est
 * ARCHIVÉ (task_audit_log), contrairement au `note` du forçage générique qui
 * n'est conservé que pour les clôtures.
 */
export const taskReopenPayloadSchema = z.object({
  reason: z.string().trim().min(3).max(2000),
});
export type TaskReopenPayload = z.infer<typeof taskReopenPayloadSchema>;

export const pvReceptionCreatePayloadSchema = z.object({
  type: z.enum(POSEUR_TASK_TYPES).default("pv_reception"),
  monday_id: z.string().min(1),
  name: z.string().optional(),
  address: z.string().optional(),
  client_first_name: z.string().optional(),
  client_last_name: z.string().optional(),
});
export type PvReceptionCreatePayload = z.infer<typeof pvReceptionCreatePayloadSchema>;

export const taskReviewPayloadSchema = z
  .object({
    decision: z.enum(["approve", "reject", "question"]),
    notes: z.string().max(4000).optional(),
    // "Approuver avec modifications" : payload corrigé par le reviewer,
    // appliqué avant l'approbation. base_rev = verrou optimiste (payload_rev).
    payload: z.record(z.unknown()).optional(),
    base_rev: z.number().int().nonnegative().optional(),
  })
  .refine(
    (d) => d.decision === "approve" || (d.notes !== undefined && d.notes.trim().length > 0),
    { message: "notes_required", path: ["notes"] },
  );

export const renderUpsertPayloadSchema = z.object({
  payload: z.record(z.unknown()),
  base_rev: z.number().int().nonnegative().optional(),
});

// ── Signature à distance ──────────────────────────────────────────────
// Création d'une demande : l'adresse est saisie/confirmée par le poseur, pas
// reprise en silence du CRM. Le CRM a déjà livré des adresses doubles
// ("a@x.fr, b@x.fr") qui ont empoisonné signatoryEmail — une adresse non
// validée ici partirait dans un email au client.
export const signatureRequestCreatePayloadSchema = z.object({
  email: z.string().trim().email().max(254),
  // Nom pré-rempli sur la page de signature ; le signataire peut le corriger.
  signatory_name: z.string().trim().min(1).max(200).optional(),
});

// Soumission depuis la page publique. `signature` est un data URL PNG produit
// par le canvas ; la borne de taille protège l'endpoint non authentifié.
export const signatureSubmitPayloadSchema = z.object({
  signature: z
    .string()
    // Une signature manuscrite pèse quelques dizaines de ko. La borne reste
    // sous la limite de corps d'express (2 Mo) pour que le refus vienne d'ici,
    // avec un code lisible, plutôt que du parseur.
    .max(500_000)
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, "signature_must_be_png_data_url"),
  signatory_name: z.string().trim().min(1).max(200),
});

export const mediaRegistrationPayloadSchema = z.object({
  client_id: z.string().min(1).max(120),
  kind: renderMediaKindSchema,
  storage_path: z.string().min(1),
  mime_type: z.string().min(1).optional(),
  size_bytes: z.number().int().nonnegative().optional(),
  width: z.number().int().nonnegative().optional(),
  height: z.number().int().nonnegative().optional(),
  caption: z.string().max(500).optional(),
});

// ── Sync ──────────────────────────────────────────────────────────────
const syncBase = z.object({
  client_mutation_id: z.string().min(1).max(120),
  task_id: z.string().uuid(),
  created_at: isoDateTimeSchema,
});

export const syncMutationSchema = z.discriminatedUnion("kind", [
  syncBase.extend({ kind: z.literal("accept") }),
  syncBase.extend({ kind: z.literal("refuse"), reason: z.string().min(1).max(2000) }),
  syncBase.extend({ kind: z.literal("start") }),
  syncBase.extend({
    kind: z.literal("update_render"),
    payload: z.record(z.unknown()),
    base_rev: z.number().int().nonnegative().optional(),
  }),
  syncBase.extend({
    kind: z.literal("register_media"),
    media: mediaRegistrationPayloadSchema,
  }),
  syncBase.extend({
    kind: z.literal("remove_media"),
    client_id: z.string().min(1).max(120),
  }),
  syncBase.extend({ kind: z.literal("submit") }),
  syncBase.extend({ kind: z.literal("resume") }),
]);

export const syncFlushRequestSchema = z.object({
  mutations: z.array(syncMutationSchema).max(200),
});

// ── Facturation relais ────────────────────────────────────────────────
export const billingQuerySchema = z.object({
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  assignee_ids: z.array(z.string().uuid()).optional(),
});

export const priceUpdateSchema = z.object({
  task_ids: z.array(z.string().uuid()).min(1),
  price: z.number().min(0).nullable(),
});

// Tarif par défaut d'un type de mission (hérité par les missions sans prix propre).
export const taskTypePriceUpdateSchema = z.object({
  type: taskTypeSchema,
  price: z.number().min(0),
});

// Une tournée regroupe 2+ missions d'un même relais qui partagent un kilométrage.
export const tourCreateSchema = z.object({
  assignee_id: z.string().uuid(),
  total_km: z.number().min(0),
  task_ids: z.array(z.string().uuid()).min(2),
  label: z.string().max(120).optional(),
});
